import * as ToastPrimitive from "@radix-ui/react-toast";
import { Dices as DicesIcon } from "lucide-react";
import { useToastStore } from "~/stores/useToastStore";
import { useState, useEffect } from "react";

export default function ToastContainer() {
  const { toasts, removeToast } = useToastStore();
  const [cookieConsentVisible, setCookieConsentVisible] = useState(false);

  useEffect(() => {
    // Check if cookie consent is visible
    const checkCookieConsent = () => {
      const consentData = localStorage.getItem("cookie-consent");
      if (!consentData) {
        setCookieConsentVisible(true);
        return;
      }

      try {
        const { accepted, timestamp } = JSON.parse(consentData);
        if (!accepted || !timestamp) {
          setCookieConsentVisible(true);
          return;
        }

        const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
        const consentAge = Date.now() - timestamp;
        setCookieConsentVisible(consentAge > oneYearInMs);
      } catch {
        setCookieConsentVisible(true);
      }
    };

    checkCookieConsent();

    // Listen for storage changes (when consent is accepted)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "cookie-consent") {
        checkCookieConsent();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Also check periodically in case consent was set in the same window
    const interval = setInterval(checkCookieConsent, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {toasts.map((toast) => (
        <ToastPrimitive.Root
          key={toast.id}
          open={true}
          onOpenChange={(open) => {
            if (!open) removeToast(toast.id);
          }}
          duration={2000}
          style={{
            animation: 'toast-slide-in 150ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
          className="bg-gray-950 border-2 border-purple-500 text-gray-100 rounded-lg p-4 shadow-lg flex items-center gap-3 min-w-[200px] pointer-events-auto"
        >
          <div
            className="shrink-0 inline-block"
            style={{
              animation: 'dice-shake-global 600ms ease-in-out infinite',
              transformOrigin: 'center center',
              willChange: 'transform',
            }}
          >
            <DicesIcon className="w-5 h-5 text-purple-400" />
          </div>
          <ToastPrimitive.Title className="text-sm font-medium">
            {toast.message}
          </ToastPrimitive.Title>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport
        className={`fixed bottom-0 right-0 flex flex-col gap-2 p-6 m-0 list-none z-50 outline-none max-w-[100vw] pointer-events-none ${
          cookieConsentVisible ? 'pb-44 md:pb-48' : 'pb-24 md:pb-32'
        }`}
      />
    </ToastPrimitive.Provider>
  );
}
